// Deterministic colour assignment for the locations map / sidebar.
// Same dimension value → same colour every render, so the user can
// build a mental "blue = Branch / green = Warehouse" model.

import type { Site } from '@cmms/types'

export type ColorBy = 'none' | 'entity' | 'state' | 'category'

// Two palettes tuned to read well against the basemap in each mode.
// Light-mode swatches are saturated mid-tones; dark-mode swatches are
// lighter so they pop against the inverted dark tiles.
const PALETTE_LIGHT = [
  '#0D9488', '#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6',
  '#EC4899', '#10B981', '#F97316', '#6366F1', '#14B8A6',
  '#A855F7', '#DC2626', '#0EA5E9', '#84CC16', '#F43F5E',
  '#06B6D4', '#CA8A04', '#7C3AED', '#22C55E', '#DB2777',
]
const PALETTE_DARK = [
  '#5EEAD4', '#FBBF24', '#FCA5A5', '#C4B5FD', '#93C5FD',
  '#F9A8D4', '#6EE7B7', '#FDBA74', '#A5B4FC', '#2DD4BF',
  '#D8B4FE', '#F87171', '#7DD3FC', '#BEF264', '#FDA4AF',
  '#67E8F9', '#FDE047', '#C4B5FD', '#86EFAC', '#F472B6',
]

const UNSPECIFIED_LIGHT = '#64748B' // slate-500
const UNSPECIFIED_DARK  = '#94A3B8' // slate-400

export function getDimensionValue(site: Site, by: ColorBy): string | null {
  switch (by) {
    case 'entity':   return site.entity?.trim() || null
    case 'state':    return site.state?.trim() || null
    case 'category': return site.category?.trim() || null
    default:         return null
  }
}

/** Stable hash → palette index, so same string always picks same colour. */
function hash(str: string): number {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function colorForValue(value: string | null, isDark: boolean): string {
  const unspecified = isDark ? UNSPECIFIED_DARK : UNSPECIFIED_LIGHT
  if (!value) return unspecified
  const palette = isDark ? PALETTE_DARK : PALETTE_LIGHT
  return palette[hash(value) % palette.length] ?? unspecified
}

export interface LegendEntry { value: string | null; color: string; count: number }

/** Build the {value → color} legend for the current site set + dimension.
 *  Sorted by descending count so the largest buckets show first. */
export function buildLegend(sites: Site[], by: ColorBy, isDark: boolean): LegendEntry[] {
  if (by === 'none') return []
  const counts = new Map<string | null, number>()
  for (const s of sites) {
    const v = getDimensionValue(s, by)
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, color: colorForValue(value, isDark), count }))
    .sort((a, b) => {
      // Unspecified always sinks to the bottom.
      if (a.value == null) return 1
      if (b.value == null) return -1
      return b.count - a.count
    })
}

export const COLOR_BY_LABELS: Record<ColorBy, string> = {
  none:     'None',
  entity:   'Entity',
  state:    'State',
  category: 'Category',
}
